import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Send, X, Loader2, Mail } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useSupportAssistant } from '../../context/SupportAssistantContext';
import { getMarketingLocale } from '../../lib/marketingLocale';
import { isSupportWidgetRoute } from '../../config/supportAssistantRoutes';
import { askSupportAssistant } from '../../services/supportAssistantApi';
import { trackEvent } from '../../lib/analytics';
import { contactMailto } from '../../config/contactChannels';
import {
  MESSAGE_MAX_CHARS,
  isMessageValid,
  buildAskPayload,
  classifyAssistantResponse,
  resolveFallbackEmail,
  buildOpenAnalyticsParams,
  buildQuestionAnalyticsParams,
  buildFallbackAnalyticsParams,
  buildHumanEmailAnalyticsParams,
} from './supportAssistantLogic';

/**
 * MB-AI-SUPPORT-2C — Widget UI de l'assistant support SUPERKAWA.
 *
 * Un seul composant, deux modes (jamais 4 composants distincts, section 2 du
 * ticket) :
 *  - variant="floating"  : bouton flottant + fenêtre de chat, marketing uniquement,
 *                          visible seulement sur les routes de l'allowlist.
 *  - variant="embedded"  : zone "Posez votre question" montée en haut d'un
 *                          Help Sheet existant (OS/POS/App Client), pas de
 *                          bouton flottant, pas d'ouverture/fermeture propre.
 *
 * N'appelle jamais Mistral ni aucun fournisseur directement : uniquement
 * POST /api/support-assistant/ask via supportAssistantApi (services/).
 * N'envoie jamais shop_id/user_id/rôle/contenu de page — payload strict
 * {message, surface, pathname, language} (section 6).
 * Aucun historique persistant : messages en state React uniquement,
 * jamais localStorage/sessionStorage/IndexedDB (section 15).
 */

const COPY = {
  fr: {
    openLabel: 'Vous avez une question ?',
    closeLabel: 'Fermer l’assistant',
    title: 'Assistant SUPERKAWA',
    embeddedTitle: 'Posez votre question',
    placeholder: 'Posez votre question…',
    emptyHint: 'Une réponse basée sur la documentation SUPERKAWA pour cette page.',
    send: 'Envoyer',
    counterNear: (remaining) => `${remaining} caractères restants`,
    unavailable: 'L’assistant est momentanément indisponible.',
    noAnswer: 'Je n’ai pas trouvé de réponse précise à cette question sur cette page.',
    quota: 'Vous avez atteint la limite de questions pour aujourd’hui.',
    error: 'Une erreur est survenue. Réessayez plus tard.',
    contactPrefix: 'Écrivez-nous : ',
    you: 'Vous',
    humanFallbackLink: 'Besoin d’un humain ? Écrivez-nous par email',
  },
  en: {
    openLabel: 'Have a question?',
    closeLabel: 'Close the assistant',
    title: 'SUPERKAWA Assistant',
    embeddedTitle: 'Ask a question',
    placeholder: 'Ask your question…',
    emptyHint: 'An answer based on SUPERKAWA’s documentation for this page.',
    send: 'Send',
    counterNear: (remaining) => `${remaining} characters left`,
    unavailable: 'The assistant is temporarily unavailable.',
    noAnswer: 'I couldn’t find a precise answer to this question on this page.',
    quota: 'You’ve reached today’s question limit.',
    error: 'Something went wrong. Please try again later.',
    contactPrefix: 'Email us: ',
    you: 'You',
    humanFallbackLink: 'Need a human? Email us directly',
  },
};

const COUNTER_THRESHOLD = 60; // n'affiche le compteur que proche de la limite (section 5).

let messageIdSeq = 0;
function nextMessageId() {
  messageIdSeq += 1;
  return messageIdSeq;
}

function AssistantMessageBody({ text }) {
  // Rendu texte simple : pas de HTML brut (section 16). Retours à la ligne
  // préservés, listes "- " reconnues visuellement sans parser Markdown.
  return (
    <>
      {text.split('\n').map((line, i) => (
        <p key={i} className={i > 0 ? 'mt-1' : ''}>{line}</p>
      ))}
    </>
  );
}

export default function SupportAssistantWidget({ surface, variant = 'embedded' }) {
  const location = useLocation();
  const { language: contextLanguage } = useLanguage();
  const pathname = location.pathname;
  const language = surface === 'marketing'
    ? getMarketingLocale(pathname)
    : (contextLanguage === 'en' ? 'en' : 'fr');
  const c = COPY[language] || COPY.fr;

  const isFloating = variant === 'floating';
  const { floatingOpen, setFloatingOpen, pendingMessage, consumePendingMessage } = useSupportAssistant();
  const [localOpen, setLocalOpen] = useState(variant === 'embedded');
  // Le mode floating délègue son état ouvert/fermé au contexte partagé
  // (MB-AI-SUPPORT-3) — un CTA ailleurs dans l'app peut ainsi l'ouvrir sans
  // dupliquer d'état ; le mode embedded reste local, gouverné par le Help Sheet parent.
  const open = isFloating ? floatingOpen : localOpen;
  const setOpen = isFloating ? setFloatingOpen : setLocalOpen;
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (isFloating && !isSupportWidgetRoute(pathname)) {
      setOpen(false);
    }
  }, [isFloating, pathname, setOpen]);

  useEffect(() => {
    if (isFloating && open && pendingMessage) {
      setInputValue(pendingMessage);
      consumePendingMessage();
    }
  }, [isFloating, open, pendingMessage, consumePendingMessage]);

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackEvent('support_assistant_open', buildOpenAnalyticsParams({ surface, pathname, language }));
    }
    if (open && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open, surface, pathname, language]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    if (!isFloating || !open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFloating, open, setOpen]);

  if (isFloating && !isSupportWidgetRoute(pathname)) {
    return null;
  }

  const remaining = MESSAGE_MAX_CHARS - inputValue.length;
  const canSend = isMessageValid(inputValue) && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    const question = inputValue.trim();
    setInputValue('');
    setMessages((prev) => [...prev, { id: nextMessageId(), role: 'user', text: question }]);
    setSending(true);
    trackEvent('support_assistant_question', buildQuestionAnalyticsParams({ surface, pathname, language }));

    const payload = buildAskPayload({ message: question, surface, pathname, language });

    try {
      const { data } = await askSupportAssistant(payload);
      const kind = classifyAssistantResponse(data.status, data.reason);
      if (kind === 'answer') {
        setMessages((prev) => [...prev, { id: nextMessageId(), role: 'assistant', kind, text: data.answer }]);
      } else {
        const email = resolveFallbackEmail(surface, data.contact_email);
        const text = kind === 'quota' ? c.quota : kind === 'no_answer' ? c.noAnswer : c.unavailable;
        setMessages((prev) => [...prev, { id: nextMessageId(), role: 'assistant', kind, text, email }]);
        trackEvent('support_assistant_fallback', buildFallbackAnalyticsParams({ surface, pathname, language, status: kind }));
      }
    } catch (_err) {
      const email = resolveFallbackEmail(surface, null);
      setMessages((prev) => [...prev, { id: nextMessageId(), role: 'assistant', kind: 'error', text: c.error, email }]);
      trackEvent('support_assistant_fallback', buildFallbackAnalyticsParams({ surface, pathname, language, status: 'error' }));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const conversation = (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
      {messages.length === 0 && (
        <p className="text-[13px] text-[#6B7C74] leading-relaxed" data-testid="support-assistant-empty-hint">{c.emptyHint}</p>
      )}
      {messages.map((m) => (
        <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
          <div
            className={
              m.role === 'user'
                ? 'max-w-[85%] rounded-2xl rounded-tr-sm bg-[#2F5D46] text-white px-3 py-2 text-[13.5px]'
                : 'max-w-[85%] rounded-2xl rounded-tl-sm bg-[#F3F1EA] text-[#1E1E1E] px-3 py-2 text-[13.5px]'
            }
            data-testid={m.role === 'user' ? 'support-assistant-message-user' : 'support-assistant-message-assistant'}
          >
            <AssistantMessageBody text={m.text} />
            {m.role === 'assistant' && m.email && (
              <a
                href={`mailto:${m.email}`}
                className="mt-1.5 flex items-center gap-1.5 text-[12.5px] font-medium text-[#2F5D46] underline underline-offset-2"
                data-testid="support-assistant-fallback-email"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={2} />
                {c.contactPrefix}{m.email}
              </a>
            )}
          </div>
        </div>
      ))}
      {sending && (
        <div className="flex justify-start">
          <div className="rounded-2xl rounded-tl-sm bg-[#F3F1EA] px-3 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-[#6B7C74]" data-testid="support-assistant-loading" />
          </div>
        </div>
      )}
    </div>
  );

  const composer = (
    <div className="border-t border-[#E8E5DE] px-3 py-2.5">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.slice(0, MESSAGE_MAX_CHARS))}
          onKeyDown={handleKeyDown}
          maxLength={MESSAGE_MAX_CHARS}
          rows={1}
          placeholder={c.placeholder}
          aria-label={c.placeholder}
          className="flex-1 resize-none rounded-xl border border-[#E8E5DE] bg-white px-3 py-2 text-[13.5px] leading-snug text-[#1E1E1E] placeholder:text-[#9a9a9a] focus:outline-none focus:ring-1 focus:ring-[#2F5D46] max-h-24"
          data-testid="support-assistant-input"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          aria-label={c.send}
          className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#2F5D46] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#274d3a] transition-colors"
          data-testid="support-assistant-send"
        >
          <Send className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
      {remaining <= COUNTER_THRESHOLD && (
        <p className="mt-1 text-[11px] text-[#9a9a9a]" data-testid="support-assistant-counter">
          {c.counterNear(Math.max(remaining, 0))}
        </p>
      )}
    </div>
  );

  if (!isFloating) {
    // Mode embedded : pas de bouton flottant, pas d'état ouvert/fermé propre
    // — la visibilité est entièrement gouvernée par le Help Sheet parent
    // (section 7/8/9 : jamais de bouton flottant permanent dans OS/POS/App Client).
    return (
      <div
        className="flex flex-col border border-[#E8E5DE] rounded-2xl bg-white overflow-hidden"
        data-testid="support-assistant-embedded"
      >
        <div className="px-4 pt-3 pb-2 border-b border-[#E8E5DE]">
          <p className="text-[13.5px] font-semibold text-[#1E1E1E]">{c.embeddedTitle}</p>
        </div>
        <div className="flex flex-col" style={{ maxHeight: '280px' }}>
          {conversation}
          {composer}
        </div>
      </div>
    );
  }

  return (
    <>
      {open && (
        <div
          className="fixed z-[110] inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-20 w-full sm:w-[360px] max-h-[75vh] sm:max-h-[520px] flex flex-col bg-white sm:rounded-2xl rounded-t-2xl shadow-[0_12px_40px_rgba(30,30,30,0.18)] border border-[#E8E5DE] pb-[env(safe-area-inset-bottom)]"
          role="dialog"
          aria-label={c.title}
          aria-modal="false"
          data-testid="support-assistant-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E5DE] flex-shrink-0">
            <p className="text-[14px] font-semibold text-[#1E1E1E]">{c.title}</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={c.closeLabel}
              className="w-7 h-7 rounded-full flex items-center justify-center text-[#6B7C74] hover:bg-[#F3F1EA] transition-colors"
              data-testid="support-assistant-close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          {conversation}
          {composer}
          <div className="flex-shrink-0 px-4 pb-2.5 pt-0.5 text-center">
            <a
              href={contactMailto}
              onClick={() => trackEvent('support_human_email_click', buildHumanEmailAnalyticsParams({ surface, pathname, language }))}
              className="inline-flex items-center gap-1.5 text-[11.5px] text-[#9a9a9a] hover:text-[#6B7C74] transition-colors"
              data-testid="support-assistant-human-link"
            >
              <Mail className="w-3 h-3" strokeWidth={2} />
              {c.humanFallbackLink}
            </a>
          </div>
        </div>
      )}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={c.openLabel}
          className="fixed z-[110] bottom-4 right-4 flex items-center gap-2 rounded-full bg-[#2F5D46] text-white pl-4 pr-5 py-3 shadow-[0_8px_24px_rgba(47,93,70,0.35)] hover:bg-[#274d3a] transition-colors"
          data-testid="support-assistant-fab"
        >
          <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2} />
          <span className="text-[13.5px] font-medium">{c.openLabel}</span>
        </button>
      )}
    </>
  );
}
